import Phaser from "phaser";
import type { WorldTheme } from "@/levels/worldTypes";

/**
 * Shared vector-graphics helpers for a world's visual identity (a small
 * sphere icon + optional faint backdrop), used identically by World
 * Select, Level Select, and the in-game HUD so the "language" never
 * drifts between screens. Pure Phaser Graphics - no external assets, no
 * new dependency, nothing here runs per-frame (every caller draws once
 * and leaves the result alone).
 */

/** Deterministic pseudo-random in [0, 1) - stable across re-renders (resize, scene rebuild) without depending on the core engine's RNG. */
function hashRandom(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** A stable, per-world seed for `drawWorldBackdrop()`, so it's deterministic (never a different scatter on every re-render) without depending on the core engine's RNG. Shared by every caller of `drawWorldBackdrop()` (World Select, and PR31's in-gameplay board backdrop) so the same world always seeds identically wherever it's drawn. */
export function worldSeed(worldId: string): number {
  let seed = 0;
  for (let i = 0; i < worldId.length; i++) seed += worldId.charCodeAt(i) * (i + 1);
  return seed;
}

/**
 * Draws this world's small sphere icon, centered at (cx, cy) with the
 * given radius. An exhaustive switch (not `if/else`) on purpose - a
 * future world (Saturn, Sun, ...) that forgets to add its own branch here
 * fails to compile instead of silently rendering another world's artwork.
 */
export function drawWorldIcon(g: Phaser.GameObjects.Graphics, theme: WorldTheme, cx: number, cy: number, radius: number): void {
  switch (theme.symbol) {
    case "moon":
      drawMoonIcon(g, theme, cx, cy, radius);
      return;
    case "earth":
      drawEarthIcon(g, theme, cx, cy, radius);
      return;
    case "mars":
      drawMarsIcon(g, theme, cx, cy, radius);
      return;
    case "saturn":
      drawSaturnIcon(g, theme, cx, cy, radius);
      return;
    case "sun":
      drawSunIcon(g, theme, cx, cy, radius);
      return;
    default: {
      // Exhaustiveness check: a `WorldSymbol` value with no case above
      // fails to compile here, rather than silently falling through to
      // another world's icon at runtime.
      const exhaustive: never = theme.symbol;
      throw new Error(`Unhandled world symbol: ${exhaustive}`);
    }
  }
}

/** Silver/gray sphere with 2-3 minimal craters. */
function drawMoonIcon(g: Phaser.GameObjects.Graphics, theme: WorldTheme, cx: number, cy: number, radius: number): void {
  g.fillStyle(theme.accent, 1);
  g.fillCircle(cx, cy, radius);
  g.lineStyle(Math.max(1, radius * 0.05), theme.secondary, 0.8);
  g.strokeCircle(cx, cy, radius);

  const craters: { dx: number; dy: number; r: number }[] = [
    { dx: -0.32, dy: -0.28, r: 0.22 },
    { dx: 0.3, dy: 0.02, r: 0.16 },
    { dx: -0.08, dy: 0.38, r: 0.13 },
  ];
  for (const crater of craters) {
    g.fillStyle(theme.contour, 0.3);
    g.fillCircle(cx + crater.dx * radius, cy + crater.dy * radius, crater.r * radius);
  }
}

/** Blue/turquoise sphere with a couple of soft green "continent" blobs and a faint cloud arc. */
function drawEarthIcon(g: Phaser.GameObjects.Graphics, theme: WorldTheme, cx: number, cy: number, radius: number): void {
  g.fillStyle(theme.accent, 1);
  g.fillCircle(cx, cy, radius);

  g.fillStyle(theme.contour, 0.85);
  g.fillEllipse(cx - radius * 0.28, cy - radius * 0.12, radius * 0.62, radius * 0.4);
  g.fillEllipse(cx + radius * 0.32, cy + radius * 0.34, radius * 0.4, radius * 0.26);
  g.fillEllipse(cx - radius * 0.06, cy + radius * 0.55, radius * 0.28, radius * 0.15);

  g.lineStyle(Math.max(1, radius * 0.07), 0xf5fbff, 0.3);
  g.beginPath();
  g.arc(cx, cy, radius * 0.8, Phaser.Math.DegToRad(-150), Phaser.Math.DegToRad(-55));
  g.strokePath();

  g.lineStyle(Math.max(1, radius * 0.05), theme.secondary, 0.9);
  g.strokeCircle(cx, cy, radius);
}

/** Rust/orange sphere with darker crater and canyon-line contour detail. */
function drawMarsIcon(g: Phaser.GameObjects.Graphics, theme: WorldTheme, cx: number, cy: number, radius: number): void {
  g.fillStyle(theme.accent, 1);
  g.fillCircle(cx, cy, radius);

  const craters: { dx: number; dy: number; r: number }[] = [
    { dx: -0.3, dy: -0.22, r: 0.18 },
    { dx: 0.26, dy: 0.14, r: 0.14 },
  ];
  for (const crater of craters) {
    g.fillStyle(theme.contour, 0.4);
    g.fillCircle(cx + crater.dx * radius, cy + crater.dy * radius, crater.r * radius);
  }

  // A single canyon-line streak across the lower half - Mars's own detail
  // beyond craters alone, distinct from the moon's crater-only surface.
  g.lineStyle(Math.max(1, radius * 0.1), theme.contour, 0.5);
  g.beginPath();
  g.arc(cx, cy, radius * 0.55, Phaser.Math.DegToRad(20), Phaser.Math.DegToRad(160));
  g.strokePath();

  g.lineStyle(Math.max(1, radius * 0.05), theme.secondary, 0.9);
  g.strokeCircle(cx, cy, radius);
}

/**
 * Muted gold sphere with a pale, restrained ring - Saturn's own identity,
 * distinct from every other world's craters/canyons/continents. The ring
 * ellipse is drawn BEFORE the sphere, so the sphere's fill naturally
 * covers its middle third and leaves the two "ears" - the same simple,
 * recognizable silhouette every other world icon here uses (a flat fill
 * plus a couple of restrained contour details, no real 3D occlusion).
 */
function drawSaturnIcon(g: Phaser.GameObjects.Graphics, theme: WorldTheme, cx: number, cy: number, radius: number): void {
  g.lineStyle(Math.max(1, radius * 0.14), theme.accent, 0.7);
  g.strokeEllipse(cx, cy, radius * 2.7, radius * 0.85);
  g.lineStyle(Math.max(1, radius * 0.06), theme.contour, 0.4);
  g.strokeEllipse(cx, cy, radius * 2.7, radius * 0.85);

  g.fillStyle(theme.accent, 1);
  g.fillCircle(cx, cy, radius);

  // A single faint band-line for surface texture, restrained on purpose.
  g.lineStyle(Math.max(1, radius * 0.06), theme.contour, 0.3);
  g.beginPath();
  g.arc(cx, cy, radius * 0.6, Phaser.Math.DegToRad(200), Phaser.Math.DegToRad(340));
  g.strokePath();

  g.lineStyle(Math.max(1, radius * 0.05), theme.secondary, 0.9);
  g.strokeCircle(cx, cy, radius);
}

/**
 * Radiant solar disc with a short corona-ray crown - Sun's own identity,
 * deliberately NOT a ringed silhouette (never confused with Saturn's
 * icon). A hot-white core fade (contour tone) sits inside the accent-fill
 * disc, short alternating rays radiate outward at 12 evenly-spaced
 * angles, and a secondary-tone stroke closes the disc - the same "flat
 * fill + a couple of restrained contour details + one outline stroke"
 * convention every other world icon here uses.
 */
function drawSunIcon(g: Phaser.GameObjects.Graphics, theme: WorldTheme, cx: number, cy: number, radius: number): void {
  const rayCount = 12;
  for (let i = 0; i < rayCount; i++) {
    const angle = (i / rayCount) * Math.PI * 2;
    const innerR = radius * 1.05;
    // Alternating short/long rays read as a corona crown rather than a
    // uniform gear/sun-icon cliche.
    const outerR = radius * (i % 2 === 0 ? 1.45 : 1.25);
    g.lineStyle(Math.max(1, radius * 0.09), theme.accent, 0.75);
    g.beginPath();
    g.moveTo(cx + Math.cos(angle) * innerR, cy + Math.sin(angle) * innerR);
    g.lineTo(cx + Math.cos(angle) * outerR, cy + Math.sin(angle) * outerR);
    g.strokePath();
  }

  g.fillStyle(theme.accent, 1);
  g.fillCircle(cx, cy, radius);

  // Hot-white core fade, restrained - the pale contour tone bleeding
  // toward center, never a full second sphere.
  g.fillStyle(theme.contour, 0.35);
  g.fillCircle(cx, cy, radius * 0.55);

  g.lineStyle(Math.max(1, radius * 0.05), theme.secondary, 0.9);
  g.strokeCircle(cx, cy, radius);
}

/** Faint scattered points (stars, for the moon) confined to `bounds` - deterministic per `seed`, so it looks identical across re-renders. */
export function scatterStars(
  g: Phaser.GameObjects.Graphics,
  bounds: { x: number; y: number; width: number; height: number },
  count: number,
  color: number,
  seed: number,
): void {
  for (let i = 0; i < count; i++) {
    const x = bounds.x + hashRandom(seed + i * 7.13) * bounds.width;
    const y = bounds.y + hashRandom(seed + i * 13.71 + 1) * bounds.height;
    const r = 0.6 + hashRandom(seed + i * 3.3 + 2) * 1.1;
    g.fillStyle(color, 0.3 + hashRandom(seed + i * 1.7) * 0.35);
    g.fillCircle(x, y, r);
  }
}

/** Faint scattered arcs (light/cloud hints, for Earth) confined to `bounds` - deterministic per `seed`. */
export function scatterArcs(
  g: Phaser.GameObjects.Graphics,
  bounds: { x: number; y: number; width: number; height: number },
  count: number,
  color: number,
  seed: number,
): void {
  for (let i = 0; i < count; i++) {
    const cx = bounds.x + hashRandom(seed + i * 5.5) * bounds.width;
    const cy = bounds.y + hashRandom(seed + i * 8.2 + 1) * bounds.height;
    const r = 8 + hashRandom(seed + i * 2.1) * 16;
    g.lineStyle(1.5, color, 0.14 + hashRandom(seed + i * 4.4) * 0.12);
    g.beginPath();
    g.arc(cx, cy, r, 0, Math.PI * 1.4);
    g.strokePath();
  }
}

/**
 * This world's faint backdrop decoration (stars for the moon, arcs for
 * Earth and Mars), confined to `bounds`. An exhaustive switch for the
 * same reason as `drawWorldIcon` above.
 */
export function drawWorldBackdrop(
  g: Phaser.GameObjects.Graphics,
  theme: WorldTheme,
  bounds: { x: number; y: number; width: number; height: number },
  seed: number,
): void {
  switch (theme.symbol) {
    case "moon":
      scatterStars(g, bounds, 14, 0xffffff, seed);
      return;
    case "earth":
      scatterArcs(g, bounds, 5, theme.contour, seed);
      return;
    case "mars":
      // Faint canyon-line hints, reusing the same arc primitive as
      // Earth's cloud arcs but in Mars's own contour tone - subtle
      // geological texture, no new drawing primitive required.
      scatterArcs(g, bounds, 6, theme.contour, seed);
      return;
    case "saturn":
      // Pale, sparse orbital-arc hints (fewer/fainter than Earth's own
      // cloud arcs) - restrained, not a starfield - plus a light star
      // scatter, reused verbatim from the moon's own primitive.
      scatterStars(g, bounds, 8, 0xffffff, seed);
      scatterArcs(g, bounds, 3, theme.accent, seed + 100);
      return;
    case "sun":
      // Warm heat-shimmer hints - the same arc primitive as Earth/Mars,
      // in Sun's own hot accent tone, sparser than Earth's cloud arcs so
      // it reads as radiant heat, not weather.
      scatterArcs(g, bounds, 4, theme.accent, seed);
      return;
    default: {
      const exhaustive: never = theme.symbol;
      throw new Error(`Unhandled world symbol: ${exhaustive}`);
    }
  }
}
